import React, { useState } from 'react';
import {
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Box
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Logo from '../common/Logo';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface AdminAuthProps {}

const AdminAuth: React.FC<AdminAuthProps> = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Call the real admin login API endpoint
      const response = await axios.post(`${API_BASE_URL}/api/auth/admin-login`, {
        email,
        password
      });

      if (response.data.success && response.data.token) {
        // Store the real JWT token from the backend
        // Note: This only stores adminToken, it does NOT affect the regular user's token
        // Both tokens can coexist in localStorage (adminToken for admin, token for regular users)
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('adminUser', JSON.stringify({
          email: response.data.user.email,
          role: response.data.user.role || 'admin',
          name: response.data.user.fullName || 'Admin User',
          permissions: response.data.user.permissions || ['read', 'write', 'delete', 'admin']
        }));
        
        navigate('/admin/dashboard');
      } else {
        setError(response.data.message || 'Login failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Admin login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" style={{ marginTop: '100px' }}>
      <Paper 
        elevation={3} 
        style={{ 
          padding: '40px',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        <Box style={{ textAlign: 'center', marginBottom: '30px' }}>
          <Logo size="large" />
          <Typography variant="h4" style={{ marginTop: '20px', fontWeight: 'bold' }}>
            Admin Panel
          </Typography>
          <Typography variant="body1" style={{ marginTop: '10px', color: '#666' }}>
            Founder Access Only
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" style={{ marginBottom: '20px' }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ marginBottom: '20px' }}
            placeholder="founder@corpcrunch.io"
          />
          
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ marginBottom: '30px' }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            style={{
              backgroundColor: '#000',
              color: '#fff',
              padding: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              borderRadius: '8px'
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Access Admin Panel'}
          </Button>
        </form>

        <Box style={{ marginTop: '20px', textAlign: 'center' }}>
          <Typography variant="body2" style={{ color: '#666' }}>
            Authorized access only for founder@corpcrunch.io
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminAuth;